// ============================================================================
// PAGE - simulator.jsx
// ============================================================================
// client/src/pages/lebEngine/ardunioSimulator.jsx

import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import showNotification from '../../utilities/showNotification';
import MonacoCodeEditor from '../../components/labEngine/monacoCodeEditor';
import ProfIXChatbot from '../../components/chatBot/ProfIXChatbot';
import ProfileHeader from '../../components/headers/profileHeader';
import SharedHeader from '../../components/headers/sharedHeader1';
import CustomButton from '../../components/customs/customButton';
import CustomAlert from '../../components/customs/customAlert';
import BackButton from '../../components/customs/backButton';
import { CourseDomains } from '../../types/courseDomains';
import SensorSimulation from '../../modals/labEngine/sensorSimulation';
import SimulationReport from '../../modals/labEngine/simulationReport';
import { GlobalContext } from '../../context/provider';
import {
  useCourse, 
  useComponentLibrary, 
  useVisualCircuit, 
  useSimulator
} from '../../hooks'


import '../../styles/globalstyles.css';
import './arduinoSimulator.css';

const ArduinoSimulator = () => {
  
  const { courseId } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('sessionId');

  const navigate = useNavigate();
  const alertShownRef = useRef(false);
  const editorRef = useRef(null);
  const clearedAtRef = useRef(null);

  // State management
  const [circuit, setCircuit] = useState(null);
  const [simulator, setSimulator] = useState(null);
  const [code, setCode] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [serialOutput, setSerialOutput] = useState([]);
  const [autoScroll, setAutoScroll] = useState(true);
  const [outputInterval, setOutputInterval] = useState(null);
  const [stateInterval, setStateInterval] = useState(null);
  const [ledInterval, setLedInterval] = useState(null); // ✅ ADD THIS
  const [ledState, setLedState] = useState('LOW');
  const [actuatorStates, setActuatorStates] = useState({});
  const [actuatorStateInterval, setActuatorStateInterval] = useState(null);
  
  // ✅ NEW: Live component states
  const [componentStates, setComponentStates] = useState({
    sensors: [],
    actuators: []
  });

  // Alert states
  const [alertVisible, setAlertVisible] = useState(false);
  const [alertConfig, setAlertConfig] = useState({});

  // Course/Lab info
  const [courseInfo, setCourseInfo] = useState(null);
  
  // After other state declarations (around line 50)
  const [sensorSimulationVisible, setSensorSimulationVisible] = useState(false);
  const [selectedSensorForConfig, setSelectedSensorForConfig] = useState(null);
  const [sensorReadingsHistory, setSensorReadingsHistory] = useState([]);

  const [showValidationPanel, setShowValidationPanel] = useState(false);
  const [validationResults, setValidationResults] = useState(null);
  const [activeValidationTab, setActiveValidationTab] = useState('all');
  const [lastValidationResults, setLastValidationResults] = useState(null);

  const [showSimulationReport, setShowSimulationReport] = useState(false);

  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  const [isValidatingCode, setIsValidatingCode] = useState(false);
  const [isCompiled, setIsCompiled] = useState(false);
  const [isStartingSimulation, setIsStartingSimulation] = useState(false);
  const [compileStatus, setCompileStatus] = useState('idle'); // 'idle' | 'compiling' | 'success' | 'error'
  const [codeChanged, setCodeChanged] = useState(false);
  const [loopDelay, setLoopDelay] = useState(2000);

  const [iterationCount, setIterationCount] = useState(0);

  // Hooks
  const {
    currentSimulator,
    isLoading: simulatorLoading,
    error: simulatorError,
    getSimulatorBySessionId,
    getSimulatorById,
    createSimulator,
    startSimulator,
    validateSimulator,
    stopSimulator,
    pauseSimulator,
    resumeSimulator,
    resetSimulator,
    uploadCode: uploadCodeToSimulator,
    getSerialOutput,
    updateSensorConfig,
    getSimulatorStatus,
    mergeComponentTemplates,
    saveGradingPayload,
    submitGrades,  
    getActuatorStates,
    sendSerialInput,
    addSimulationSnapshot,
    clearError: clearSimulatorError,
  } = useSimulator();

  const {
    currentCircuit,
    getCircuitBySessionId,
    getCircuitById,
    getCircuitByCircuitId
  } = useVisualCircuit();

  const { getCourseById } = useCourse();

  const { getComponentById } = useComponentLibrary();

  // Global context
  const {
    authState: { authData },
  } = useContext(GlobalContext);

  useEffect(() => {
    const loadData = async () => {
      try {
        if (!authData || !authData.id) {
          console.log('⏳ Waiting for authData...');
          return;
        }

        if (!sessionId) {
          console.error('❌ No sessionId provided in URL');
          alert('⚠️ Invalid simulator link. SessionId is required.');
          return;
        }

        console.log('🔍 Loading simulator for sessionId:', sessionId);

        // 1. Load course info
        if (courseId) {
          const course = await getCourseById(courseId);
          if (course.success) {
            setCourseInfo(course.data);
          }
        }

        // 2. Load simulator by sessionId
        const simResponse = await getSimulatorBySessionId(sessionId);
        
        if (simResponse.success) {
          setSimulator(simResponse.data);
          console.log('✅ Simulator loaded:', simResponse.data.simulatorId);
          
          // 3. Load circuit by sessionId
          const circuitResponse = await getCircuitBySessionId(sessionId);
          console.log('🔍 Circuit Response:', circuitResponse);

          if (circuitResponse.success) {
            // ✅ Fetch and attach component library data to each component
            const enrichedCircuit = { ...circuitResponse.data };
            enrichedCircuit.components = await Promise.all(
              circuitResponse.data.components.map(async (comp) => {
                if (comp.componentId) {
                  try {
                    const compLibResponse = await getComponentById(comp.componentId);
                    if (compLibResponse?.success) {
                      return {
                        ...comp,
                        componentLibraryData: compLibResponse.data  // ✅ Add library data here
                      };
                    }
                  } catch (err) {
                    console.error('Error fetching component library data:', err);
                  }
                }
                return comp;
              })
            );
            setCircuit(enrichedCircuit);
            
            setIsGeneratingCode(true);
            try {
              console.log('🔄 Reloading code from templates...');
              showNotification('🤖 Prof-iX is generating code...', 'info');
              const defaultCode = simResponse.data.uploadedCode || await generatePrimaryCodeWithAI(circuitResponse.data, simResponse.data);
              setCode(defaultCode);
              
              showNotification('Code loaded!', 'success');
            } catch (err) {
              console.error('Error reloading code:', err);
              showNotification('Failed to regenerate code!', 'error');
            } finally {
              setIsGeneratingCode(false);  // Hide loading
            }
          } else {
            // ✅ ADD THIS:
            console.error('❌ Failed to load circuit:', circuitResponse.error);
            alert(`⚠️ Failed to load circuit: ${circuitResponse.error || 'Unknown error'}`);
          }
          
          // 6. Check if this is a graded lab session
          console.log(sessionId.includes('_LAB') ? '✅ Graded lab session' : 'ℹ️ Practice mode (no grading)');
        } else {
          console.error('❌ Simulator not found for sessionId:', sessionId);
          alert('⚠️ Simulator not found. Please sync your circuit to simulator first.');
          navigate(-1); // Go back
        }

      } catch (err) {
        console.error('❌ Error loading simulator page:', err);
        console.error('❌ Error stack:', err.stack);
        alert('Failed to load simulator');
      }
    };

    loadData();
  }, [sessionId, courseId, authData?.id]);

  useEffect(() => {
    // Initialize component states as soon as circuit is loaded
    if (circuit && circuit.components) {
      console.log('🔧 Initializing component states from circuit...');
      
      const sensors = circuit.components
        .filter(c => c.category === 'sensors')
        .map(circuitComp => ({
          id: circuitComp.instanceId,
          name: circuitComp.label || circuitComp.componentName,
          type: circuitComp.componentName,
          icon: circuitComp.icon || '🌡️',
          componentId: circuitComp.componentId,
          currentValue: null,  // No data yet
          lastUpdate: null
        }));
      
      const actuators = circuit.components
        .filter(c => c.category === 'actuators')
        .map(circuitComp => {
          // const pinMatch = circuitComp.pins.find(p => p.pinName.includes('GPIO'));
          const pinMatch = circuitComp.pinDetails.find(p => p.pinName.includes('GPIO'));
          const pinNumber = pinMatch?.pinName.match(/\d+/)?.[0];
          
          return {
            id: circuitComp.instanceId,
            name: circuitComp.label || circuitComp.componentName,
            type: circuitComp.componentName,
            icon: circuitComp.icon || '⚡',
            pin: pinNumber,
            state: null,  // No state yet
            mode: 'OUTPUT',
            lastUpdate: null
          };
        });
      
      setComponentStates(prev => ({ ...prev, sensors, actuators }));
      console.log('✅ Initial component states set:', { sensors, actuators });
    }
  }, [circuit]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopSerialMonitorPolling();
      stopStatePolling(); 
    };
  }, []);

  useEffect(() => {
    if (autoScroll && serialOutput.length > 0) {
      const outputElement = document.querySelector('.serial-output-content');
      if (outputElement) {
        outputElement.scrollTop = outputElement.scrollHeight;
      }
    }
  }, [serialOutput, autoScroll]);

  // Add after other useEffects (around line 300)
  useEffect(() => {
    // ✅ Ensure serialOutput stays as array
    if (!Array.isArray(serialOutput)) {
      console.error('❌ serialOutput corrupted! Resetting to array.');
      setSerialOutput([]);
    }
  }, [serialOutput]);

  // Add after other handlers (around line 500)
  const handleSensorClick = (sensor) => {
    // ✅ ADD: Debug log
    console.log('🖱️ Sensor clicked:', sensor);
    console.log('🔍 Attached sensors:', simulator?.attachedSensors);

    // ✅ Find the attached sensor from simulator to get simulationConfig
    const attachedSensor = simulator?.attachedSensors?.find(
      // s => s.type === sensor.type
      s => s.sensorId === sensor.id
    );
    
    // ✅ Merge sensor display data with simulation config
    const sensorWithConfig = {
      ...sensor,
      simulationConfig: attachedSensor?.simulationConfig || null
    };
    
    console.log('📋 Sensor with config:', sensorWithConfig);
    setSelectedSensorForConfig(sensorWithConfig);
    // setSelectedSensorForConfig(sensor);
    setSensorSimulationVisible(true);
  };

  const handleSaveSensorConfig = async (sensorId, config) => {
    if (!simulator) return;
    
    try {
      // ✅ ADD: Debug logging
      console.log('💾 Saving sensor config:', { sensorId, config });
      console.log('🔍 Simulator ID:', simulator._id);
      
      const response = await updateSensorConfig(simulator._id, sensorId, config);
      
      // ✅ ADD: Log response
      console.log('📡 Save response:', response);
      
      if (response.success) {
        showNotification('Sensor configuration saved!', 'success');
        // Refresh simulator data
        const statusResponse = await getSimulatorStatus(simulator._id);
        if (statusResponse.success) {
          setSimulator(statusResponse.data);
        }
      } else {
        // ✅ IMPROVED: Better error message
        const errorMsg = response.error || response.message || 'Unknown error';
        alert(`❌ Failed to save config: ${errorMsg}`);
      }
    } catch (err) {
      console.error('Error saving sensor config:', err);
      // ✅ IMPROVED: Show actual error
      alert(`❌ Failed to save configuration: ${err.message}`);
    }
  };

  const generatePrimaryCodeWithAI = async (circuit, simulator) => {
    try {
      console.log('🔍 DEBUG: Starting code generation...');
      
      // ✅ Extract simulator configs first
      const simulatorConfigs = {
        wireless: simulator?.attachedWireless || [],
        communication: simulator?.attachedCommunication || [],
        azureIoT: simulator?.attachedAzureIoT || [],  // ✅ ADD
        azureAnalytics: simulator?.attachedAzureAnalytics || [],  // ✅ ADD
        azureStorage: simulator?.attachedAzureStorage || [],  // ✅ ADD
        azureVisualization: simulator?.attachedAzureVisualization || [],  // ✅ ADD
        security: simulator?.attachedSecurity || [],
        modules: simulator?.attachedModules || []
      };

      console.log('🔍 DEBUG: Simulator configs:', simulatorConfigs);

      // ✅ Get all component IDs, but filter out DISABLED communication components
      const componentIds = circuit.components
        .filter(comp => {
          // Keep all non-communication and non-azure components
          if (comp.category !== 'communication' && 
              comp.category !== 'azure_iot' && 
              comp.category !== 'azure_analytics' && 
              comp.category !== 'azure_storage' && 
              comp.category !== 'azure_visualization') {
            return true;
          }
          
          // For communication components, check if enabled
          if (comp.category === 'communication') {
            const commConfig = simulatorConfigs.communication.find(
              c => c.componentId === comp.componentId
            );
            
            if (!commConfig) {
              console.warn(`⚠️ Communication component ${comp.componentId} not found in simulator`);
              return false;
            }
            
            if (!commConfig.config?.enabled) {
              console.log(`⚠️ ${comp.componentName} is DISABLED - will show comment only`);
              return false;
            }
            
            console.log(`✅ ${comp.componentName} is ENABLED - will include code`);
            return true;
          }
          
          // For Azure IoT components, check if enabled
          if (comp.category === 'azure_iot') {
            const azureConfig = simulatorConfigs.azureIoT.find(
              az => az.componentId === comp.componentId
            );
            
            if (!azureConfig) {
              console.warn(`⚠️ Azure IoT component ${comp.componentId} not found in simulator`);
              return false;
            }
            
            if (!azureConfig.config?.enabled) {
              console.log(`⚠️ ${comp.componentName} is DISABLED - will show comment only`);
              return false;
            }
            
            console.log(`✅ ${comp.componentName} is ENABLED - will include code`);
            return true;
          }
          
          // For other Azure components (analytics, storage, visualization)
          if (comp.category === 'azure_analytics') {
            const azureConfig = simulatorConfigs.azureAnalytics.find(
              az => az.componentId === comp.componentId
            );
            return azureConfig?.config?.enabled !== false;
          }
          
          if (comp.category === 'azure_storage') {
            const azureConfig = simulatorConfigs.azureStorage.find(
              az => az.componentId === comp.componentId
            );
            return azureConfig?.config?.enabled !== false;
          }
          
          if (comp.category === 'azure_visualization') {
            const azureConfig = simulatorConfigs.azureVisualization.find(
              az => az.componentId === comp.componentId
            );
            return azureConfig?.config?.enabled !== false;
          }
          
          return true;
        })
        .map(comp => comp.componentId)
        .filter(Boolean);

      if (componentIds.length === 0) {
        console.warn('⚠️ No components in circuit');
        return '// No components in circuit\nvoid setup() {}\nvoid loop() {}';
      }

      console.log('🔍 DEBUG: Component IDs for merge:', componentIds);

      // ✅ Pass configs to merge
      const response = await mergeComponentTemplates(
        componentIds,
        circuit.board || 'ESP32',
        simulatorConfigs
      );

      if (response.success) {
        console.log(`✅ Code merged for ${response.data.componentCount} components`);
        return response.data.mergedCode;
      } else {
        console.error('❌ Code merge failed:', response.error);
        return '// Code generation failed: ' + response.error + '\nvoid setup() {}\nvoid loop() {}';
      }

    } catch (error) {
      console.error('❌ Error generating code:', error);
      return '// Error generating code: ' + error.message + '\nvoid setup() {}\nvoid loop() {}';
    }
  };

  const handleUploadCode = async () => {
    if (!simulator) {
      alert('⚠️ No simulator available');
      return;
    }

    if (!code.trim()) {
      alert('⚠️ Please write some code first');
      return;
    }

    try {
      const response = await uploadCodeToSimulator(simulator._id, code, 'Arduino');
      if (response.success) {
        // alert('✅ Code uploaded successfully!');
        showNotification('Code uploaded successfully!', 'success');
      } else {
        // alert(`❌ Failed to upload code: ${response.error}`);
        showNotification(`Failed to upload code: ${response.error}`, 'error');
      }
    } catch (err) {
      console.error('Error uploading code:', err);
      // alert('❌ Failed to upload code');
      showNotification('Failed to upload code!', 'error');
    }
  };

  const handleDownloadValidation = () => {
    let content = 'VALIDATION RESULTS\n';
    content += '==================\n\n';
    
    if (validationResults.categorizedErrors?.length > 0) {
      content += '❌ ERRORS\n';
      content += '---------\n';
      validationResults.categorizedErrors.forEach(cat => {
        content += `\n${cat.label}\n`;
        cat.messages.forEach(msg => {
          content += `• ${msg}\n`;
        });
      });
    }
    
    if (validationResults.categorizedWarnings?.length > 0) {
      content += '\n⚠️ WARNINGS\n';
      content += '-----------\n';
      validationResults.categorizedWarnings.forEach(cat => {
        content += `\n${cat.label}\n`;
        cat.messages.forEach(msg => {
          content += `• ${msg}\n`;
        });
      });
    }
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'validation-results.txt';
    a.click();
    URL.revokeObjectURL(url);
  };
  
  const handleCompile = async () => {
    closeValidationPanels();
    if (!simulator || !code.trim()) return;

    // setShowValidationPanel('false');
    setCompileStatus('compiling');
    setIsCompiled(false);

    try {
      const simulatorId = simulator._id;
      // await resetSimulator(simulatorId);
      await uploadCodeToSimulator(simulatorId, code, 'Arduino');

      setIsValidatingCode(true);
      let response;
      try {
        showNotification('🤖 Prof-iX is validating code...', 'info');
        response = await validateSimulator(simulatorId);
      } finally {
        setIsValidatingCode(false);
      }

      if (response.success) {
        setCompileStatus('success');
        setIsCompiled(true);
        setCodeChanged(false);
        setLoopDelay(response.validation?.loopDelay || 2000);
        showNotification('Compilation successful!', 'success');

        if (response.validation?.hasWarnings) {
          setValidationResults({
            categorizedErrors: [],
            categorizedWarnings: response.validation.categorizedWarnings,
            isValid: true
          });
          setShowValidationPanel(true);
        }
      } else {
        setCompileStatus('error');
        setIsCompiled(false);
        if (response.validation?.hasErrors) {
          setValidationResults({
            categorizedErrors: response.validation.categorizedErrors || [],
            categorizedWarnings: response.validation.categorizedWarnings || [],
            isValid: false
          });
          setShowValidationPanel(true);
        } else {
          showNotification(`Compilation failed: ${response.message}`, 'error');
        }
      }
    } catch (err) {
      setCompileStatus('error');
      setIsCompiled(false);
      const categorizedErrors = err.response?.data?.validation?.categorizedErrors || [];
      const categorizedWarnings = err.response?.data?.validation?.categorizedWarnings || [];
      if (categorizedErrors.length > 0) {
        setValidationResults({
          categorizedErrors,
          categorizedWarnings,
          isValid: false
        });
        setShowValidationPanel(true);
        setActiveValidationTab('all');
        // ✅ No notification needed - panel shows the details
      } else {
        const errorMessage = err.response?.data?.message || err.message;
        showNotification(`❌ Compilation failed: ${errorMessage}`, 'error');
      }
    }
  };

  const handleRun = async () => {
    closeValidationPanels();
    if (!isCompiled) return;
    if (!simulator) return;

    setSensorReadingsHistory([]);
    setIterationCount(0);
    setIsStartingSimulation(true);
    try {
      await resetSimulator(simulator._id);
      showNotification('🤖 Prof-iX is starting simulation...', 'info');
      const response = await startSimulator(simulator._id);

      if (response.success) {
        alertShownRef.current = false;
        setIsRunning(true);
        setSerialOutput([{ timestamp: new Date(), message: '🤖 Prof-iX started simulation', type: 'INFO' }]);

        const delay = response.validation?.loopDelay || loopDelay;
        startSerialMonitorPolling(delay);
        startStatePolling(delay);
        startActuatorPolling(delay);
        showNotification('🤖 Prof-iX is running simulation!', 'success');
      } else {
        showNotification(`Failed to run: ${response.message}`, 'error');
      }
    } catch (err) {
      showNotification(`Failed to run: ${err.message}`, 'error');
    } finally {
      setIsStartingSimulation(false);
    }
  };

  const handlePauseSimulation = async () => {
    if (!simulator) return;

    try {
      const simulatorId = simulator._id;
      
      if (isPaused) {
        // RESUME - Backend and Frontend
        console.log('▶️ Resuming simulation...');
        
        const response = await resumeSimulator(simulatorId); // ✅ Resume backend
        if (response.success) {
          const loopDelay = code.match(/delay\s*\((\d+)\)/) ? 
            parseInt(code.match(/delay\s*\((\d+)\)/)[1]) : 2000;
          
          startSerialMonitorPolling(loopDelay); // Resume frontend polling
          startStatePolling(loopDelay);
          setIsPaused(false);
          showNotification('Simulation resumed!', 'success');
        }
        
      } else {
        // PAUSE - Backend and Frontend
        console.log('⏸️ Pausing simulation...');
        
        const response = await pauseSimulator(simulatorId); // ✅ Pause backend
        if (response.success) {
          stopSerialMonitorPolling(); // Pause frontend polling
          stopStatePolling();
          // stopLEDPolling();
          stopActuatorPolling();
          setIsPaused(true);
          showNotification('Simulation paused!', 'info');
        }
      }
    } catch (err) {
      console.error('Error pausing/resuming simulation:', err);
      showNotification('Failed to pause/resume simulation!', 'error');
    }
  };

  const handleResetSimulation = async () => {
    closeValidationPanels();
    if (!simulator) return;

    setAlertConfig({
      title: 'Reset Simulator',
      icon: '🔄',
      message: 'This will clear all output and reset the simulator. Continue?',
      onConfirm: async () => {
        try {
          const simulatorId = simulator._id; // ✅ Use _id
          
          if (!simulatorId) {
            console.error('❌ No simulator _id found!');
            return;
          }

          const response = await resetSimulator(simulatorId);
          if (response.success) {
            setSerialOutput([]);
            setIsCompiled(false);
            setCompileStatus('idle');
            setCodeChanged(false);
            if (circuit && circuit.components) {
              const sensors = circuit.components
                .filter(c => c.category === 'sensors')
                .map(circuitComp => ({
                  id: circuitComp.instanceId,
                  name: circuitComp.label || circuitComp.componentName,
                  type: circuitComp.componentName,
                  icon: circuitComp.icon || '🌡️',
                  componentId: circuitComp.componentId,
                  currentValue: null,
                  lastUpdate: null
                }));
              
              // ✅ ADD: Reset actuators too
              const actuators = circuit.components
                .filter(c => c.category === 'actuators')
                .map(circuitComp => {
                  // const pinMatch = circuitComp.pins.find(p => p.connectedTo?.includes('GPIO'));
                  const pinMatch = circuitComp.pinDetails.find(p => p.connectedTo?.includes('GPIO'));
                  const pinNumber = pinMatch?.connectedTo?.match(/GPIO(\d+)/)?.[1];
                  
                  return {
                    id: circuitComp.instanceId,
                    name: circuitComp.label || circuitComp.componentName,
                    type: circuitComp.componentName,
                    icon: circuitComp.icon || '⚡',
                    pin: pinNumber,
                    state: null,
                    mode: 'OUTPUT',
                    lastUpdate: null
                  };
                });
              
              setComponentStates({ sensors, actuators }); // ✅ Include both!
            }
            setIsRunning(false);
            stopSerialMonitorPolling();
            stopStatePolling();
            // stopLEDPolling();
            stopActuatorPolling();
            // alert('✅ Simulator reset!');
            showNotification('Simulator reset!', 'success');
          }
        } catch (err) {
          console.error('Error resetting simulator:', err);
          // alert('❌ Failed to reset simulator');
          showNotification('Failed to reset simulator!', 'error');
        }
        setAlertVisible(false);
      }
    });
    setAlertVisible(true);
    setSensorReadingsHistory([]);
    setIterationCount(0);
  };

  const startSerialMonitorPolling = (loopDelay = 2000) => {
    stopSerialMonitorPolling();
    
    const pollingInterval = Math.max(loopDelay + 500, 2500);
    console.log(`📡 Starting serial polling every ${pollingInterval}ms`);
    
    const interval = setInterval(async () => {
      try {
        const response = await getSerialOutput(simulator._id, 100);

        if (response.success && Array.isArray(response.data) && response.data.length > 0) {
          // ✅ CRITICAL: Only update if data actually changed
          setSerialOutput(prev => {
            const safePrev = Array.isArray(prev) ? prev : [];
            
            // ✅ Only add truly new items (compare timestamps)
            // const newItems = response.data.filter(item => 
            //   !safePrev.some(existing => 
            //     existing.timestamp === item.timestamp && 
            //     existing.message === item.message
            //   )
            // );
            const newItems = response.data.filter(item => {
              // Filter out items older than last clear
              if (clearedAtRef.current && new Date(item.timestamp) <= clearedAtRef.current) {
                return false;
              }
              return !safePrev.some(existing =>
                existing.timestamp === item.timestamp &&
                existing.message === item.message
              );
            });
            
            if (newItems.length > 0) {
              return [...safePrev, ...newItems];
            }
            
            // ✅ CRITICAL: Return prev to prevent re-render!
            return safePrev;
          });
        }

        // Check if simulation is still running
        const statusResponse = await getSimulatorStatus(simulator._id);
        if (statusResponse.success && statusResponse.data) {
          if (!statusResponse.data.isRunning && !alertShownRef.current) {
            alertShownRef.current = true;
            clearInterval(interval);
            setOutputInterval(null);
            
            if (stateInterval) {
              clearInterval(stateInterval);
              setStateInterval(null);
            }
            stopActuatorPolling();
            setIsRunning(false);
            showNotification('Simulation completed successfully!', 'success');
          }

            if (statusResponse.data.iterationCount !== undefined) {
              setIterationCount(statusResponse.data.iterationCount);
            }
            
        }

      } catch (err) {
        if (err.response?.status === 429) {
          clearInterval(interval);
          setOutputInterval(null);
          if (stateInterval) {
            clearInterval(stateInterval);
            setStateInterval(null);
          }
          stopActuatorPolling();
          setIsRunning(false);
          showNotification('Rate limit exceeded. Simulation stopped.', 'warning');
        } else {
          console.error('Error fetching serial output:', err);
        }
      }
    }, pollingInterval);
    
    setOutputInterval(interval);
  };

  const stopSerialMonitorPolling = () => {
    if (outputInterval) {
      clearInterval(outputInterval);
      setOutputInterval(null);
    }
  };

  const startStatePolling = (loopDelay = 2000) => {
    stopStatePolling();
    
    // const pollingInterval = Math.max(loopDelay * 3, 6000);
    // const pollingInterval = Math.max(loopDelay * 2, 4000);
    const pollingInterval = Math.max(loopDelay + 500, 2500);
    console.log(`📡 Starting state polling every ${pollingInterval}ms (loopDelay: ${loopDelay}ms)`);
    
    const interval = setInterval(async () => {
      try {
        const response = await getSimulatorStatus(simulator._id);
        if (response.success && response.data) {
          updateComponentStates(response.data);
          
          // Check if simulation stopped
          if (!response.data.isRunning && !alertShownRef.current) {
            console.log('⏹️ Simulation ended - stopping all polling');
            alertShownRef.current = true;
            
            // Clear THIS interval
            clearInterval(interval);
            setStateInterval(null);
            
            // Stop output polling
            if (outputInterval) {
              clearInterval(outputInterval);
              setOutputInterval(null);
            }
            
            setIsRunning(false);
            // Don't show alert here - only in output polling
          }
        }
      } catch (err) {
        if (err.response && err.response.status === 429) {
          console.error('❌ Rate limit exceeded - stopping all polling');
          
          // Clear THIS interval
          clearInterval(interval);
          setStateInterval(null);
          
          // Stop output polling
          if (outputInterval) {
            clearInterval(outputInterval);
            setOutputInterval(null);
          }
          // stopLEDPolling();
          stopActuatorPolling();
          setIsRunning(false);
          showNotification(' Rate limit exceeded. Simulation stopped.', 'warning');
        } else {
          console.error('Error fetching component states:', err);
        }
      }
    }, pollingInterval);
    
    setStateInterval(interval);
  };

  const stopStatePolling = () => {
    if (stateInterval) {
      clearInterval(stateInterval);
      setStateInterval(null);
    }
  };

  const startActuatorPolling = (loopDelay = 2000) => {  // ✅ Accept loopDelay parameter
    stopActuatorPolling();
    
    // ✅ Use the actual loop delay from the code
    const pollInterval = Math.max(loopDelay + 500, 2500);  // Match other polling intervals
  
    console.log(`🎛️ Starting actuator polling every ${pollInterval}ms`);
    
    const interval = setInterval(async () => {
      if (!simulator?._id) return;
      
      try {
        const response = await getActuatorStates(simulator._id);
        
        console.log('📡 Actuator polling response:', response);
        
        if (response.success) {
          console.log('📊 Setting actuator states:', response.data);
          setActuatorStates(response.data);
        }
      } catch (error) {
        console.error('Error polling actuator states:', error);
      }
    }, pollInterval);

    setActuatorStateInterval(interval);
  };

  const stopActuatorPolling = () => {
    if (actuatorStateInterval) {
      clearInterval(actuatorStateInterval);
      setActuatorStateInterval(null);
    }
  };

  const updateComponentStates = (statusData) => {
    console.log('🔍 updateComponentStates called');
    console.log('📦 statusData:', statusData);
    
    if (!circuit) return;

    const sensors = [];
    const actuators = []; // ✅ ADD THIS

    const readingsSnapshot = {
      timestamp: new Date(),
      sensors: {},
      actuatorStates: {}
    };
    
    // ✅ Process sensors (existing code)
    circuit.components
      .filter(c => c.category === 'sensors')
      .forEach(circuitComp => {
        const sensorData = statusData?.attachedSensors?.find(  
          s => s.componentId === circuitComp.componentId
        );
        
        if (sensorData?.currentValue) {
          readingsSnapshot.sensors[sensorData.type] = {
            ...sensorData.currentValue
          };
        }

        sensors.push({
          id: circuitComp.instanceId,
          name: circuitComp.label || circuitComp.componentName,
          type: circuitComp.componentName,
          icon: circuitComp.icon || '🌡️',
          componentId: circuitComp.componentId,
          currentValue: sensorData?.currentValue || null, 
          lastUpdate: sensorData?.currentValue ? new Date() : null 
        });
      });

    // ✅ ADD: Process actuators
    circuit.components
      .filter(c => c.category === 'actuators')
      .forEach(circuitComp => {
        // Find actuator state from GPIO pins or attachedActuators
        const actuatorData = statusData?.attachedActuators?.find(
          a => a.componentId === circuitComp.componentId
        );
        
        // Get pin number from circuit
        // const pinMatch = circuitComp.pins.find(p => p.connectedTo?.includes('GPIO'));
        const pinMatch = circuitComp.pinDetails.find(p => p.connectedTo?.includes('GPIO'));
        const pinNumber = pinMatch?.connectedTo?.match(/GPIO(\d+)/)?.[1];
        
        // Get GPIO state if available
        const gpioPin = statusData?.gpioPins?.find(pin => pin.pinNumber === parseInt(pinNumber));
        
        actuators.push({
          id: circuitComp.instanceId,
          name: circuitComp.label || circuitComp.componentName,
          type: circuitComp.componentName,
          icon: circuitComp.icon || '⚡',
          pin: pinNumber,
          state: actuatorData?.currentState || gpioPin?.state || null,
          mode: gpioPin?.mode || 'OUTPUT',
          currentValue: actuatorData?.currentValue || null,
          lastUpdate: actuatorData?.currentValue ? new Date() : null
        });
      });

    circuit.components
      .filter(c => c.category === 'actuators')
      .forEach(circuitComp => {
        const actuatorData = statusData?.attachedActuators?.find(
          a => a.componentId === circuitComp.componentId
        );
        if (actuatorData) {
          readingsSnapshot.actuatorStates[circuitComp.componentId] = {
            currentState: actuatorData.currentState,
            currentValue: actuatorData.currentValue
          };
        }
      });

    setSensorReadingsHistory(prev => [...prev, readingsSnapshot]);
    
    if (simulator?._id) {
      addSimulationSnapshot(simulator._id, readingsSnapshot).catch(err => {
        console.error('Failed to save snapshot:', err);
      });
    }
    
    console.log('📊 Final sensors:', sensors);
    console.log('⚡ Final actuators:', actuators); // ✅ ADD LOG
    setComponentStates({ sensors, actuators }); // ✅ INCLUDE actuators!
  };

  const handlePostToIoTSession = async (freshSim = null) => {
    const sim = freshSim || simulator;   // ✅ same pattern as IIoT
    const isGradedLab = sessionId && sessionId.includes('_LAB');
    if (!isGradedLab || !sim) return;

    try {
      const duration = sim?.state?.uptime || 0;
      const gradingData = {
        serialOutput:          sim.serialOutput          || [],
        serialInput:           sim.serialInput           || [],
        uploadedCode:          sim.uploadedCode          || '',
        actuatorStates:        sim.actuatorStates        || {},
        attachedSensors:       sim.attachedSensors       || [],
        attachedActuators:     sim.attachedActuators     || [],
        codeValidationResults: sim.codeValidationResults || [],
        simulationSnapshots:   sim.simulationSnapshots   || [],
      };

      const response = await saveGradingPayload(sim._id, duration, gradingData);
      if (response.success) {
        setSimulator(prev => ({ ...prev, gradingPayload: response.data }));
        showNotification('Output posted to simulator for grading!', 'success');
      } else {
        showNotification(`Failed to post output: ${response.error}`, 'error');
      }
    } catch (err) {
      showNotification('Failed to post output', 'error');
    }
  };

  const handleGrade = async () => {
    let freshSimulator = simulator;
    const res = await getSimulatorBySessionId(sessionId);
    if (res.success) {
      freshSimulator = res.data;
      setSimulator(res.data);
    }
    await handlePostToIoTSession(freshSimulator);  // ✅ pass it in, same as IIoT
    setShowSimulationReport(true);
  };

  const handleStopSimulation = async () => {
    if (!simulator) return;

    try {
      const simulatorId = simulator._id; // ✅ Use _id
      
      if (!simulatorId) {
        console.error('❌ No simulator _id found!');
        return;
      }

      const response = await stopSimulator(simulatorId);
      if (response.success) {
        setIsRunning(false);
        setIsPaused(false);
        stopSerialMonitorPolling();
        stopStatePolling();
        setIsCompiled(false);
        setCompileStatus('idle');
        stopActuatorPolling();
        
        // if (sessionId?.includes('_LAB') && !simulator?.gradingPayload && serialOutput.length > 0) {
        //   await handlePostToIoTSession();
        // }
        if (sessionId?.includes('_LAB') && !simulator?.gradingPayload) {
          const res = await getSimulatorBySessionId(sessionId);
          if (res.success) {
            setSimulator(res.data);
            await handlePostToIoTSession(res.data);  // ✅ pass fresh
          }
        }
        
        // alert('✅ Simulation stopped!');
        showNotification('Simulation stopped!', 'success'); 
      }
    } catch (err) {
      console.error('Error stopping simulation:', err);
      // alert('❌ Failed to stop simulation');
      showNotification('Failed to stop simulation!', 'error');
    }
  };

  const handleSubmitGrades = async () => {
    if (!simulator?._id) return;
    const response = await submitGrades(simulator._id);
    if (response.success) {
      setSimulator(prev => ({ ...prev, gradingPayload: response.data }));
      showNotification('Grades submitted!', 'success');
    } else {
      showNotification(`Failed to submit grades: ${response.error}`, 'error');
    }
  };

  // Download .ino file
  const handleDownloadCode = () => {
    if (!code.trim()) {
      alert('⚠️ No code to download');
      return;
    }

    const filename = `${circuit?.name || 'sketch'}.ino`;
    const blob = new Blob([code], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleReloadCode = async () => {
    if (!circuit) {
      alert('⚠️ No circuit available');
      return;
    }

    setAlertConfig({
      title: 'Regenerate Code',
      icon: '🔄',
      message: 'This will replace your current code with fresh code from component templates. Any changes you made will be lost. Continue?',
      onConfirm: async () => {
        setAlertVisible(false);  // Close alert first
        setIsGeneratingCode(true);  // Show loading
        
        try {
          console.log('🔄 Regenerating the code from templates...');
          showNotification('🤖 Prof-iX is generating code...', 'info');
          const simResponse = await getSimulatorBySessionId(sessionId);
          // const freshCode = simResponse.data.uploadedCode || await generatePrimaryCodeWithAI(circuit, simResponse.data);    
          const freshCode = await generatePrimaryCodeWithAI(circuit, simResponse.data);
          setCode(freshCode);
          await uploadCodeToSimulator(simResponse.data._id, freshCode);
          
          showNotification('Code reloaded from templates!', 'success');
        } catch (err) {
          console.error('Error reloading code:', err);
          showNotification('Failed to regenerate code!', 'error');
        } finally {
          setIsGeneratingCode(false);  // Hide loading
        }
      }
    });
    setAlertVisible(true);
  };

  // ✅ Add serial input handler
  const handleSerialInput = async (input) => {
    console.log('📨 Serial input received:', input);
    
    // Display locally
    setSerialOutput(prev => [...prev, {
      timestamp: new Date(),
      message: `> ${input}`,
      type: 'INPUT'
    }]);
    
    // Send to backend
    try {
      const response = await sendSerialInput(simulator._id, input);
      
      if (!response.success) {
        console.error('Failed to send serial input:', response.error);
      }
    } catch (err) {
      console.error('Error sending serial input:', err);
    }
  };

  // ✅ ADD THIS: Debounce utility function at the TOP of the file (outside the component, around line 10)
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  const debouncedApiUpdate = useRef(
    debounce(async (simulatorId, sensorId, updatedConfig) => {
      try {
        console.log('🔄 Debounced API call - Saving config:', { sensorId, updatedConfig });
        const response = await updateSensorConfig(simulatorId, sensorId, updatedConfig);
        if (response.success) {
          console.log('✅ Config saved successfully');
          // ✅ DON'T refresh from server - keep local state with correct baseValues
          // The local state was already updated in handleSliderChange (lines 1052-1059)
          // Server update is just for persistence
        } else {
          console.error('❌ Failed to save config:', response.error);
        }
      } catch (err) {
        console.error('❌ Error in debounced update:', err);
      }
    }, 800) // Wait 800ms after user stops dragging
  ).current;

  const closeValidationPanels = () => {
    setShowValidationPanel(false);
  };

  // ✅ REPLACE the entire handleSliderChange function (around line 280):
  const handleSliderChange = (sensorId, valueKey, newValue) => {
    if (!simulator) return;
    
    console.log('🎚️ Slider changed:', { sensorId, valueKey, newValue });
    
    // Find the attached sensor from simulator
    const attachedSensor = simulator.attachedSensors.find(
      s => s.sensorId === sensorId
    );
    
    if (!attachedSensor) {
      console.warn('⚠️ Attached sensor not found:', sensorId);
      return;
    }
    
    if (!attachedSensor.simulationConfig) {
      console.warn('⚠️ No simulation config found for sensor:', sensorId);
      return;
    }
    
    // Create updated config with new slider value
    const updatedConfig = {
      ...attachedSensor.simulationConfig,
      manualValue: {
        ...attachedSensor.simulationConfig.manualValue,
        [valueKey]: newValue
      }
    };
    
    // ✅ Update local state immediately for smooth UI response
    setSimulator(prev => ({
      ...prev,
      attachedSensors: prev.attachedSensors.map(s => 
        s.sensorId === sensorId 
          ? { ...s, simulationConfig: updatedConfig }
          : s
      )
    }));
    
    // ✅ Debounce the API call to prevent rapid updates
    debouncedApiUpdate(simulator._id, sensorId, updatedConfig);
  };

  const profileHeaderData = {
    ...authData,
    title: '🖥️ IoT Simulator',
    subTitle: circuit?.name || 'Circuit Simulator'
  };

  // ✅ NEW: Render sensor value display
  const renderSensorValue = (sensor) => {
    if (!sensor.currentValue) {
      return <span className="sensor-value-empty">No data</span>;
    }

    if (typeof sensor.currentValue === 'object') {
      return (
        <div className="sensor-readings">
          {Object.entries(sensor.currentValue).map(([key, value]) => (
            <div key={key} className="sensor-reading-item">
              <span className="reading-label">{key}:</span>
              <span className="reading-value">{typeof value === 'number' ? value.toFixed(2) : value}</span>
            </div>
          ))}
        </div>
      );
    }

    return <span className="sensor-value">{sensor.currentValue}</span>;
  };

  const renderActuatorState = (actuator) => {
    console.log('🔍 renderActuatorState called');
    console.log('   Actuator:', actuator);
    console.log('   Actuator ID:', actuator.id);
    console.log('   All actuatorStates:', actuatorStates);
    
    // ✅ Use the full instance ID (e.g., "ACT_LED_001_1770508935722")
    const liveState = actuatorStates[actuator.id];
    
    console.log('   Looking for:', actuator.id);
    console.log('   Live state:', liveState);

    if (!liveState || !liveState.currentValues) {
      console.log('   ❌ No live state found!');
      return <span className="sensor-value-empty">No data</span>;
    }

    console.log('   ✅ Live state found:', liveState);

    // Render each output value
    return (
      <div className="actuator-live-values">
        {Object.entries(liveState.currentValues).map(([key, value]) => (
          <div key={key} className="value-row">
            <span className="value-label">{key}: </span>
            <span className="value-data">
              {(typeof value === 'boolean' || key === 'state')
                ? (value ? '🟢 HIGH' : '⚫ LOW')
                : typeof value === 'number'
                ? `${value}${key === 'angle' ? '°' : key === 'brightness' ? '%' : ''}`
                : value
              }
            </span>
          </div>
        ))}
        <small className="last-update">
          {new Date(liveState.lastUpdate).toLocaleTimeString()}
        </small>
      </div>
    );
  };

  return (
    <div className="main-container">
      <div className="main-content">

        {/* Header Section */}
        <div className="header-row">
          <ProfileHeader 
            // data={profileHeaderData}
            activePage="simulator" 
          />
          <div className="headerMiddle">
            <img src="/images/prof_ix.png" alt="Logo" className="header-logo" />
          </div>
          <SharedHeader notificationCount={5} icons={[]} />
        </div>

        {/* Toolbar */}
        <div className="ai-notebook-toolbar">
          <h3>🔣 ARDUINO IDE</h3>
          <div className="ai-notebook-toolbar-left">
            <BackButton 
                to="-1"
                activeTab=""
                label="←"
                bgColor="#6b7280"
            />
            {sessionId && sessionId.includes('_LAB') && (
              <>
                {/* Show Report — only when gradingPayload exists */}
                {simulator?.gradingPayload && (
                  <CustomButton
                    buttonTitle="📊 Show Report"
                    action={() => {
                      closeValidationPanels();
                      setShowSimulationReport(true)
                    }}
                    bgColor="#8b5cf6"
                    disabled={!simulator}
                  />
                )}
  
                {/* Grade — always shown, disabled only when graded */}
                <CustomButton
                  buttonTitle="📤 Grade"
                  action={handleGrade}
                  bgColor="#8b5cf6"
                  disabled={
                    !simulator ||
                    simulator?.gradingPayload?.status === 'graded'
                  }
                />
              </>
            )}
            {circuit && (
              <span className="ai-notebook-flow-name">
                {circuit.labId && (
                  <span className="ai-notebook-lab-tag">📚 {circuit.labId}</span>
                )}
                🧠 {circuit.name}
                <span className="ai-notebook-session-id">Session: {sessionId}</span>
              </span>
            )}
          </div>
          {/* <div className="ai-notebook-toolbar-right">
          </div> */}
        </div>

        {/* Main Simulator Layout */}
        <div className="simulator-layout">
          
          {/* Left Panel - Circuit Info */}
          {/* ✅ UPDATED: Left Panel - Live Component States */}
          <div className="circuit-info-panel">
            <h3>🔧 Live Component States</h3>
            
            {circuit ? (
              <div className="component-states-container">
                
                {/* Sensors Section */}
                {componentStates.sensors.length > 0 && (
                  <div className="component-section">
                    <h4 className="section-title">📊 Sensors</h4>
                    <div className="components-list-live">
                      
                      {componentStates.sensors.map((sensor) => {
                        // ✅ ADD: Get sensor config from simulator
                        const attachedSensor = simulator?.attachedSensors?.find(
                          // s => s.type === sensor.type
                          s => s.sensorId === sensor.id
                        );
                        const sensorConfig = attachedSensor?.simulationConfig;
                        const isManualMode = sensorConfig && sensorConfig.generationType === 'manual'; 

                        return (
                          <div 
                            key={sensor.id} 
                            className="component-card sensor-card"
                          >
                            <div 
                              className="sensor-card-header"
                              onClick={() => handleSensorClick(sensor)}
                              style={{ cursor: 'pointer' }}
                              title="Click to configure sensor"
                            >
                              <div className="component-header">
                                <span className="component-icon">{sensor.icon}</span>
                                <div className="component-info">
                                  <span className="component-name">{sensor.name}</span>
                                  {/* <span className="component-type">{sensor.type}</span> */}
                                </div>
                              {isRunning ? (
                                <span className="live-indicator">🟢</span>
                                ):(
                                <span>⚫</span>
                              )}
                              </div>
                              <div className="component-values">
                                {renderSensorValue(sensor)}
                              </div>
                            </div>
                            
                            {/* ✅ ADD: Slider for manual mode */}
                            {/* ✅ FIXED: Only show slider if config exists AND is manual mode */}
                            {isManualMode && sensorConfig?.manualValue && Object.keys(sensorConfig.manualValue).length > 0 && (
                              <div className="sensor-slider-container" onClick={(e) => e.stopPropagation()}>
                                {Object.keys(sensorConfig.manualValue).map((key) => {
                                  // Get the component data to find actual sensor range
                                  const componentData = circuit.components.find(c => c.instanceId === sensor.id);
                                  
                                  // Default values
                                  let min = 0;
                                  let max = 100;
                                  let step = 0.1;
                                  
                                  // Try to get from sensor's component library data if available
                                  if (componentData?.componentLibraryData?.simulation?.outputs) {
                                    const output = componentData.componentLibraryData.simulation.outputs.find(o => o.name === key);
                                    if (output?.range) {
                                      min = output.range.min;
                                      max = output.range.max;
                                      step = output.resolution || 0.1;
                                    }
                                  }
                                  
                                  const currentValue = sensorConfig.manualValue[key];
                                  
                                  return (
                                    <div key={key} className="slider-control">
                                      <label className="slider-label">
                                        {key}: <span className="slider-value">{currentValue}</span>
                                      </label>
                                      <input
                                        type="range"
                                        min={min}
                                        max={max}
                                        step={step}
                                        value={currentValue}
                                        onChange={(e) => handleSliderChange(sensor.id, key, parseFloat(e.target.value))}
                                        className="sensor-slider"
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Actuators Section */}
                {componentStates.actuators.length > 0 && (
                  <div className="component-section">
                    <h4 className="section-title">⚡ Actuators</h4>
                    <div className="components-list-live">
                      {componentStates.actuators.map((actuator) => (
                        <div key={actuator.id} className="component-card actuator-card">
                          <div className="component-header">
                            <span className="component-icon">{actuator.icon}</span>
                            <div className="component-info">
                              <span className="component-name">{actuator.name}</span>
                              {/* <span className="component-type">{actuator.type}</span> */}
                            </div>
                            {isRunning ? (
                              <span className="live-indicator">🟢</span>
                              ):(
                              <span>⚫</span>
                            )}
                          </div>
                          <div className="component-values">
                            {renderActuatorState(actuator)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* No Components Message */}
                {componentStates.sensors.length === 0 && componentStates.actuators.length === 0 && (
                  <div className="no-components-message">
                    <p>No sensors or actuators in this circuit</p>
                    <p className="hint">Add components in circuit designer</p>
                  </div>
                )}

                {/* Circuit Info Footer */}
                <div className="circuit-info-footer">
                  <div className="info-item">
                    <span className="info-label">Board:</span>
                    <span className="info-value">{circuit.boardType || 'ESP32'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Status:</span>
                    <span className={`status-badge ${circuit.isValid ? 'valid' : 'invalid'}`}>
                      {circuit.isValid ? '✅ Valid' : '⚠️ Invalid'}
                    </span>
                  </div>
                </div>

              </div>
            ) : (
              <div className="loading-placeholder">
                <div className="loader" />
                <p>Loading circuit...</p>
              </div>
            )}
          </div>
          
          <div className="simulator-code-area">
            {/* Center Panel - Code Editor */}
            <div className="code-editor-panel">
              <MonacoCodeEditor
                code={code}
                onChange={(value) => {
                  setCode(value);
                  setCodeChanged(true);        
                  setCompileStatus('changed');
                  setIsCompiled(false);
                }}
                onSave={handleUploadCode}
                onDownload={handleDownloadCode}
                onReload={handleReloadCode}
                onSerialInput={handleSerialInput}
                readOnly={!simulator}
                disabled={!simulator}
                fileName={circuit?.name || 'sketch'}
              />
            </div>

            {/* Right Panel - Output Console */}
            <div className="output-console-panel">
              <div className="console-header">
                <h3>📟 Serial Monitor</h3>
                <div className="console-controls">
                  <label className="auto-scroll-toggle">
                    <input
                      type="checkbox"
                      checked={autoScroll}
                      onChange={(e) => setAutoScroll(e.target.checked)}
                    />
                    Auto-scroll
                  </label>
                  <button
                    className="clear-output-btn"
                    onClick={() => {
                      clearedAtRef.current = new Date();
                      setSerialOutput([]);
                    }}
                    title="Clear output"
                  >
                    🗑️
                  </button>
                </div>
              </div>

              <div className="serial-output-content">
                {(() => {
                  // ✅ Safety check
                  if (!Array.isArray(serialOutput)) {
                    console.error('❌ serialOutput is not an array:', typeof serialOutput, serialOutput);
                    return (
                      <div className="output-error">
                        ⚠️ Serial output error. Please refresh the page.
                      </div>
                    );
                  }

                  if (serialOutput.length === 0) {
                    return (
                      <div className="output-placeholder">
                        <p>🔭 No output yet. Start the simulation to see serial output.</p>
                      </div>
                    );
                  }

                  return serialOutput.map((log, idx) => {
                    // ✅ CRITICAL: Handle both object and string formats
                    const timestamp = log.timestamp || new Date();
                    const message = typeof log === 'string' ? log : (log.message || JSON.stringify(log));
                    const type = log.type || 'INFO';

                    return (
                      <div 
                        key={`${idx}-${timestamp}`}
                        className={`output-line ${type.toLowerCase()}`}
                      >
                        <span className="output-timestamp">
                          [{new Date(timestamp).toLocaleTimeString()}]
                        </span>
                        <span className="output-message">{message}</span>
                      </div>
                    );
                  });
                })()}
              </div>

              {simulator && (
                <div className="simulator-status-bar">
                  <span>
                    Status: {isPaused ? '⏸️ Paused' : isRunning ? '🟢 Running' : '🔴 Stopped'}
                  </span>
                  <span>Uptime: {simulator.state?.uptime || 0}s</span>
                  {/* <span>Loops: {simulator.state?.loopCount || 0}</span> */}
                  <span>Loops: {iterationCount || 0}/{CourseDomains.IoT.polling.cap}</span>
                </div>
              )}
            </div>
          </div>

        </div>

        {/* Bottom - Control Buttons */}
        <div className="circuit-actions-bar">
          {/* Compile Button */}
          <CustomButton
            buttonTitle={
              compileStatus === 'compiling' ? '⏳ Compiling...' :
              compileStatus === 'success' ? '✅ Compiled' :
              compileStatus === 'error' ? '❌ Compile Error' :
              '🔨 Compile'
            }
            action={handleCompile}
            bgColor={
              compileStatus === 'changed' ? '#f97316' :
              compileStatus === 'error' ? '#ef4444' :
              compileStatus === 'success' ? '#22c55e' :
              '#3b82f6'  // ✅ blue instead of grey for idle
            }
            disabled={!simulator || !code.trim() || compileStatus === 'compiling'}
          />

          {/* Run/Stop Button */}
          <CustomButton
            buttonTitle={isRunning ? "⏹️ Stop" : "▶️ Run"}
            action={isRunning ? handleStopSimulation : handleRun}
            bgColor={isRunning ? "#ef4444" : "#10b981"}
            disabled={!isCompiled || (!isRunning && !simulator)}
          />
          {lastValidationResults && (
            <button
              className="btn-last-validation"
              onClick={() => {
                setValidationResults(lastValidationResults);
                setShowValidationPanel(true);
              }}
            >
              📋 Last Validation
            </button>
          )}

          {isRunning && !isPaused && (
            <CustomButton
              buttonTitle={"⏸️ Pause"}
              action={handlePauseSimulation}
              bgColor={"#299ce4"}
              disabled={!simulator}
            />
          )}
          {isPaused && (
            <CustomButton
              buttonTitle={"▶️ Resume"}
              action={handlePauseSimulation}
              bgColor={"#10b981"}
              disabled={!simulator}
            />
          )}
          <CustomButton
            buttonTitle="🔄 Reset"
            action={handleResetSimulation}
            bgColor="#f59e0b"
            disabled={!simulator}
          />
        </div>

        {/* Delete Confirmation Alert */}
        <CustomAlert
          visible={alertVisible}
          title={alertConfig.title}
          icon={alertConfig.icon}
          message={alertConfig.message}
          onConfirm={alertConfig.onConfirm}
          onCancel={() => setAlertVisible(false)}
        />

        {/* Error Display */}
        {simulatorError && (
          <div className="simulator-error-container">
            ❌ Error: {simulatorError}
            <button onClick={clearSimulatorError} className="error-close">
              ✖
            </button>
          </div>
        )}

        {/* Loading Overlay */}
        {simulatorLoading && (
          <div className="loading-overlay">
            <div className="loader" />
            <p>Loading simulator...</p>
          </div>
        )}

        {/* AI Code Generation Loading Overlay */}
        {isGeneratingCode && (
          <div className="ai-loading-overlay">
            <div className="ai-loading-spinner" />
            <p className="ai-loading-title">
              🤖 Prof-iX is generating your code...
            </p>
            <p className="ai-loading-subtitle">
              This may take a few seconds
            </p>
          </div>
        )}

        {/* AI Code Validation Loading Overlay */}
        {isValidatingCode && (
          <div className="ai-loading-overlay">
            <div className="ai-loading-spinner" />
            <p className="ai-loading-title">
              🤖 Prof-iX is validating your code...
            </p>
            <p className="ai-loading-subtitle">
              This may take a few seconds
            </p>
          </div>
        )}

        {/* AI Code simulation Loading Overlay */}
        {isStartingSimulation && (
          <div className="ai-loading-overlay">
            <div className="ai-loading-spinner" />
            <p className="ai-loading-title">
              🤖 Prof-iX is starting simulation...
            </p>
            <p className="ai-loading-subtitle">
              This may take a few seconds
            </p>
          </div>
        )}

      </div>

      {/* validation panel simulator page */}
      {showValidationPanel && validationResults && (() => {
        // Build flat tab list from all categories
        const errorTabs = (validationResults.categorizedErrors || []).map(cat => ({
          ...cat,
          kind: 'error'
        }));
        const warningTabs = (validationResults.categorizedWarnings || []).map(cat => ({
          ...cat,
          kind: 'warning'
        }));
        const allTabs = [...errorTabs, ...warningTabs];
        const totalCount = allTabs.reduce((sum, t) => sum + t.messages.length, 0);

        return (
          <div className="validation-panel-overlay">
            <div className="validation-panel-content">

              {/* Header */}
              <div className="validation-panel-header">
                <h3>
                  {validationResults.isValid
                    ? (validationResults.categorizedWarnings?.length > 0
                        ? '⚠️ Validation Passed with Warnings'
                        : '✅ Validation Passed')
                    : '❌ Validation Failed'}
                </h3>
                <div className="validation-header-actions">
                  <button className="validation-close-btn" onClick={() => setShowValidationPanel(false)}>
                    ✖
                  </button>
                  <button className="validation-download-btn" onClick={handleDownloadValidation}>
                    📥 Download
                  </button>
                </div>
              </div>

              {/* Tabs */}
              {allTabs.length > 0 && (() => {
                const activeItems = activeValidationTab === 'all'  // ✅ Use component-level state
                  ? allTabs
                  : allTabs.filter(t => t.label === activeValidationTab);

                return (
                  <div className="validation-tabs-container">
                    {/* Tab Bar */}
                    <div className="validation-tab-bar">
                      <button
                        className={`validation-tab ${activeValidationTab === 'all' ? 'active' : ''}`}
                        onClick={() => setActiveValidationTab('all')}
                      >
                        All <span className="validation-tab-count">{totalCount}</span>
                      </button>
                      {allTabs.map((tab, idx) => (
                        <button
                          key={idx}
                          className={`validation-tab ${activeValidationTab === tab.label ? 'active' : ''} ${tab.kind === 'error' ? 'tab-error' : 'tab-warning'}`}
                          onClick={() => setActiveValidationTab(tab.label)}
                        >
                          {tab.label} <span className="validation-tab-count">{tab.messages.length}</span>
                        </button>
                      ))}
                    </div>

                    {/* Tab Content */}
                    <div className="validation-panel-body">
                      {activeItems.map((cat, catIdx) => (
                        <div key={catIdx} className="validation-category">
                          {activeValidationTab === 'all' && (
                            <div className={`validation-category-header ${cat.kind === 'error' ? 'error-header' : 'warning-header'}`}>
                              {cat.label}
                            </div>
                          )}
                          <div className="validation-messages">
                            {cat.messages.map((message, msgIdx) => (
                              <div key={msgIdx} className={cat.kind === 'error' ? 'validation-error-item' : 'validation-warning-item'}>
                                <span className="validation-bullet">•</span>
                                <span className="validation-message-text">{message}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Success State */}
              {validationResults.isValid &&
                validationResults.categorizedWarnings?.length === 0 &&
                validationResults.categorizedErrors?.length === 0 && (
                <div className="validation-panel-body">
                  <div className="validation-success">
                    <div className="validation-success-item">
                      <span className="validation-success-icon">✅</span>
                      <span>Code structure: Valid</span>
                    </div>
                    <div className="validation-success-item">
                      <span className="validation-success-icon">✅</span>
                      <span>Required functions: Present</span>
                    </div>
                    <div className="validation-success-item">
                      <span className="validation-success-icon">✅</span>
                      <span>All components properly configured</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              {(validationResults.categorizedErrors?.length > 0 ||
                validationResults.categorizedWarnings?.length > 0) && (
                <div className="validation-actions">
                  {validationResults.isCircuitError && (
                    <button
                      className="btn-go-to-circuit"
                      onClick={() => { setShowValidationPanel(false); window.history.back(); }}
                    >
                      🔙 Go Back to Circuit Builder
                    </button>
                  )}
                  <button
                    className="btn-fix-code"
                    onClick={() => setShowValidationPanel(false)}
                  >
                    🔧 Fix Code Issues
                  </button>
                </div>
              )}

            </div>
          </div>
        );
      })()}

      {/* Sensor Configuration Modal */}
      <SensorSimulation
        visible={sensorSimulationVisible}
        sensor={selectedSensorForConfig}
        onClose={() => {
          setSensorSimulationVisible(false);
          setSelectedSensorForConfig(null);
        }}
        onSave={handleSaveSensorConfig}
      />

      <SimulationReport
        visible={showSimulationReport}
        simulator={simulator}
        circuit={circuit}
        domain="IoT"
        onClose={() => setShowSimulationReport(false)}
        // onSubmit={handlePostToIoTSession}
        onSubmit={handleSubmitGrades}
        onRefreshSimulator={async () => {
          const res = await getSimulatorBySessionId(sessionId); // ← NOT getSimulatorStatus
          console.log('🔄 Poll result status:', res?.data?.gradingPayload?.status);
          if (res.success) setSimulator(res.data);
        }}
      />

      <ProfIXChatbot 
        userName={authData.name}
        courseId={courseId} 
        courseName={courseInfo?.title} 
        category={courseInfo?.category}
      />
      
    </div>
  );
};

export default ArduinoSimulator;